import { capitalizeString } from '@/helpers/text'
import { assertNotNull } from '@/helpers/typing'
import { backend } from '@/services/backend'
import { auth } from '@/services/auth'
import { updateDocsUserDataCache, updateUserDocsCache } from '@/helpers/cache'
import { showFreeBookOfTheDay18484 } from '@/services/ab/experiments'

import type {
  Annotation,
  AnnotationContent,
  AnnotationRange,
  AnnotationWithContent,
  DocExportStats,
  Content,
  DocType,
  Doc,
  DocAudio,
  DocListItem,
  Publisher,
  Commentaries,
  ApiResultDocs,
  EmbeddedDocGroup,
  HighlightType,
  Bookmark,
  ListItemType,
  DocsUserDataResponse,
  DocUserDataWithContent,
  OpenQuestionsContent,
  DocUserData,
  NoUserData,
  UserDocListItem,
  CustomList,
} from './interfaces'

import { DocReadingSettings } from '@/models/doc.settings'
import { DocControls } from './doc.controls'
import type { BookTutorial } from './doc.tutorial'
import { DocTocView } from './doc.toc'
import { getOfflineBooks } from '@/services/native.storage'
import type { DocsListView } from './docs.list'
import { Search } from './doc.search'
import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'
import type { OpenQuestionsView } from './doc.book'
import { ReadingProgress } from '@/services/readingProgress'
import { Mixpanel } from '@/analytics/mixpanel'
import { meta } from '@/services/meta'
import { SCROLL_SHOW_THRESHOLD_MOBILE } from '@/init/settings'
import { Sentry } from '@/services/sentry'
import { router } from '@/helpers/router'
import { emitter } from '@/services/mitt'
import type { AppState } from '@capacitor/app'
import { checklist } from './checklist'
import { storage } from '@/helpers/storage'
import { StorageKeys } from '@/helpers/storage.keys'
import { ChecklistStepID } from './checklist.types'
import { Timer } from '@/helpers/timer'
/**
 * The initial value of DocsView._showLimit, which is the number of docs we
 * should initially load into the page.
 */
export const SCROLL_INITIAL_SHOW_LIMIT = 30

/**
 * The number of new docs we should load each time we add to the page.
 * 10 more documents seems to be enough on any screen size.
 */
export const SCROLL_BATCH_SIZE = 10

/**
 * Annotation data and reference to parent content.
 */
export class AnnotationView implements Annotation {
  private _content: AnnotationContent
  private _annotation: Annotation

  public id: string
  public quote: string
  public ranges: AnnotationRange[]
  public is_public: boolean
  public created: string
  public updated: string
  public highlight_type: HighlightType

  constructor(content: AnnotationContent, annotation: Annotation) {
    this._annotation = annotation
    this._content = content
    this.id = annotation.id
    this.quote = annotation.quote
    this.ranges = annotation.ranges
    this.is_public = annotation.is_public
    this.created = annotation.created
    this.updated = annotation.updated
    this.highlight_type = annotation.highlight_type
  }

  get content(): AnnotationContent {
    return this._content
  }

  get text(): string {
    return this._annotation.text ? this._annotation.text : ''
  }

  /**
   * Settter for text.
   *
   * We have getter / setter for test to make it updatable.
   * When we edit the note, we also want to update the data source for highlights,
   * which is the array of annotations in the `_content`.
   */
  set text(value: string) {
    this._annotation.text = value
  }

  /**
   * Returns chapter link params.
   */
  get contentLink(): Record<string, unknown> {
    if (this._content.doc.doc_type === 'book') {
      return {
        name: 'book.page',
        params: {
          url_slug: this._content.doc.url_slug,
          page_url_slug: this._content.url_slug,
        },
        query: {
          highlight: this._annotation.id,
        },
      }
    }
    if (this._content.doc.doc_type === 'podcast_episode') {
      return {
        name: 'podcast.page',
        params: {
          url_slug: this._content.doc.url_slug,
          page_url_slug: this._content.url_slug,
        },
      }
    }
    return {
      name: 'article',
      params: {
        url_slug: this._content.doc.url_slug,
      },
    }
  }

  /**
   * Returns chapter link params.
   */
  get publicLink(): Record<string, unknown> {
    return {
      name: 'public_highlight',
      params: {
        book_url_slug: this._content.doc.url_slug,
        highlight_id: this._annotation.id,
      },
    }
  }

  /**
   * Returns chapter title.
   */
  get contentTitle(): string {
    if (this._content.doc.doc_type === 'book') {
      return this._content.doc.title + ': ' + this._content.title
    }
    return this._content.doc.title
  }

  /**
   * Returns doc title.
   */
  get docTitle(): string {
    return this._content.doc.title
  }

  /**
   * Returns author.
   */
  get author(): string {
    return this._content.doc.author
  }

  /**
   * Returns cover image.
   */
  get cover_image(): string | undefined {
    return this._content.doc.cover_image
  }

  /**
   * Returns doc_type.
   */
  get doc_type(): string {
    return this._content.doc.doc_type
  }

  /**
   * Returns content id.
   */
  get contentId(): string {
    return this._content.id
  }
}

/**
 * Convert highlights objects (AnnotationWithContent) array
 * to highlights views (AnnotationView) array.
 */
export function highlightsToViews(
  highlights: AnnotationWithContent[],
): AnnotationView[] {
  const views: AnnotationView[] = []
  for (let i = 0; i < highlights.length; i++) {
    const highlight = highlights[i]
    const view = new AnnotationView(highlight.content, highlight)
    views.push(view)
  }

  return views
}

/**
 * Chapter piece data and logic to display content in components.
 */
export class ContentView implements Content {
  protected _doc: DocView

  id: string
  doc_id: string
  title: string
  text: string
  content_type: string
  order: number
  url_slug: string
  highlights: Annotation[]

  commentaries?: Commentaries

  accessed: string | null
  completed: string | null

  constructor(doc: DocView, content: Content) {
    this._doc = doc
    this.id = content.id
    this.doc_id = doc.id
    this.title = content.title
    this.text = content.text
    this.content_type = content.content_type
    this.order = content.order
    this.url_slug = content.url_slug
    this.highlights = content.highlights
    this.accessed = content.accessed
    this.completed = content.completed
    this.commentaries = content.commentaries
  }

  /**
   * Returns CSS class for page icon in the sidebar.
   */
  get iconCssClass(): string {
    return ''
  }

  public get doc(): DocView {
    return this._doc
  }

  public asSerialisable(): AnnotationContent {
    const doc = {
      id: this.doc.id,
      title: this.doc.title,
      author: this.doc.author,
      cover_image: this.doc.cover_image,
      url_slug: this.doc.url_slug,
      doc_type: this.doc.doc_type,
    }
    return {
      id: this.id,
      doc: doc,
      title: this.title,
      content_type: this.content_type,
      order: this.order,
      url_slug: this.url_slug,
    }
  }

  /**
   * This is a shorthand to get `doc.isChapterEOC`.
   */
  public get isChapterEOC(): boolean {
    return this.doc.isChapterEOC
  }
}

/**
 * Chapter piece data and logic to display content in components.
 */
export class ChapterView extends ContentView implements Content {
  /**
   * Returns CSS class for page icon in the sidebar.
   */
  get iconCssClass(): string {
    return 'fas fa-book text-gray'
  }
}

/**
 * Returns CTA block item for Books and Articles views.
 */
export function createCtaBlockItem(idx?: number): DocListItem {
  if (idx === null || idx === undefined) {
    idx = 0
  }
  // Create Doc with all of the required fields
  // title: 'CTA' is used to check whether this is a CTA block, don't change it
  return {
    id: `call-to-action-id-${idx}`,
    title: 'CTA',
    author: 'CTA Author',
    cover_image: '',
    url_slug: 'call-to-action',
    public_url_slug: 'call-to-action-public',
    is_free: true,
    is_free_botd_18484: false,
    is_ai: false,
    amazon_link: '',
    amazon_image: '',
    doc_type: 'article',
    forum_id: 'forum-cta-id',
    tags: [''],
    popularity_read_count: 0,
    popularity_user_count: 0,
    created: '2020-12-02',
    first_published_at: '2020-12-02',
    has_audio: false,
    user: getEmptyUserData(),
  }
}

/**
 * Document data and logic to display doc in components.
 */
export class DocView implements Doc {
  protected _pages: ContentView[]

  public id!: string
  public title!: string
  public author!: string
  public blurb?: string
  public cover_image?: string
  public url_slug!: string
  public public_url_slug!: string
  public is_free!: boolean
  public is_free_botd_18484!: boolean
  public amazon_link!: string
  public amazon_image!: string
  public tags!: string[]

  public doc_type!: DocType

  // Reading settings.
  public settings: DocReadingSettings

  public has_audio!: boolean

  public doc_export_stats?: DocExportStats
  public audio?: DocAudio
  public publisher?: Publisher

  public original_publish_date?: string
  public original_url?: string
  public original_words?: number
  public summary_words?: number

  public forum_id!: string

  public popularity_user_count!: number
  public popularity_read_count!: number
  public created!: string
  public first_published_at!: string | null
  public doc_group?: EmbeddedDocGroup
  public is_ai: boolean = false

  public content: Content[]

  public controls: DocControls
  public tutorial: BookTutorial | null
  public toc: DocTocView

  public user: DocUserDataWithContent

  /**
   * Number of the current page displayed in the UI.
   */
  protected _pageNum: number
  protected _pagesMap: { [url_slug: string]: ContentView }

  public readingStartTime: number = 0
  public accumulatedReadTime: number = 0
  public isReadingTimePaused: boolean = true
  readingProgress: ReadingProgress | null = null
  streakTimer: Timer | null = null
  // For tests, to permanently disable timers,
  // can be set by `disableTimers()` method call.
  timersDisabled: boolean = false

  checklistReadTimeStepInterval19055: number | undefined = undefined

  /** Whether or not the audio player is active. This is controlled from Player.vue. */
  isAudioPlaying: boolean = false

  /**
   * Updates the reading time, to be used when we navigate away from this page.
   * Reading time logic:
   * We detect changes when document visibility changes.
   * When document is visible -> We save current date as readingStartTime.
   * When document is not visible -> We check time difference between now and
   * readingStartTime and add it to accumulated time.
   * When document is closed -> We check time difference and add the accumulated time.
   */
  async updateReadTime(): Promise<void> {
    const timeDiffInSeconds = Math.floor(this.totalActiveReadingTime / 1000)
    await Mixpanel.trackDocReadTime(this, timeDiffInSeconds)
    emitter.off('appVisibilityChange', this.handleVisibilityChange)
  }

  get totalActiveReadingTime(): number {
    if (this.isReadingTimePaused) {
      return this.accumulatedReadTime
    } else {
      return this.accumulatedReadTime + (Date.now() - this.readingStartTime)
    }
  }

  handleVisibilityChange = (visibility: AppState): void => {
    if (!visibility.isActive) {
      this.pauseReadingTime()
    } else {
      this.resumeReadingTime()
    }
  }

  pauseReadingTime(): void {
    // We play the audio in the background, so we don't pause the reading time.
    if (this.isReadingTimePaused || this.isAudioPlaying) {
      return
    }
    this.streakTimer?.pause()
    this.readingProgress?.pauseProgressTimer()
    const now = Date.now()
    const diff = now - this.readingStartTime
    this.accumulatedReadTime += diff
    this.isReadingTimePaused = true
  }

  resumeReadingTime(): void {
    if (!this.isReadingTimePaused) {
      return
    }
    this.streakTimer?.resume()
    this.readingProgress?.resumeProgressTimer()
    this.readingStartTime = Date.now()
    this.isReadingTimePaused = false
  }

  disableTimers(): void {
    this.timersDisabled = true
    if (this.readingProgress) {
      assertNotNull(this.readingProgress).clearTimers()
    }
  }

  restartTracking(): void {
    if (!this.timersDisabled) {
      assertNotNull(this.readingProgress).restartTracking()
    }
  }

  constructor(doc: NoUserData<Doc>, userData: DocUserDataWithContent) {
    _copyDoc(doc, this)
    this.user = userData

    this.content = []
    this._pages = new Array<ContentView>()
    this._pagesMap = {}

    doc.content.forEach((content: any) => {
      this.setContentUserData(content, userData)
      this.content.push(content)

      const contentView = this.createChapter(content)
      this._pages.push(contentView)
      this._pagesMap[content.url_slug] = contentView
    })

    this._pageNum = 0

    this.settings = new DocReadingSettings()
    this.controls = new DocControls(this)
    // Create tutorial and toc (optional, present only for book)
    this.tutorial = this.createTutorial()
    this.toc = this.createToc()
  }

  private setContentUserData(
    content: Content | OpenQuestionsContent,
    userData?: DocUserDataWithContent,
  ): void {
    const contentUserData = userData?.content?.find(
      (item) => item.id === content.id,
    )

    content.accessed = contentUserData?.accessed || null
    content.completed = contentUserData?.completed || null
    content.highlights = contentUserData?.highlights || []

    if ('questions' in content) {
      content.workouts = contentUserData?.workouts || []
    }
  }

  async initPage(
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    loadDocument: (route: RouteLocationNormalized) => Promise<void>,
  ): Promise<void> {
    // Called when the route that renders this component has changed,
    // but this component is reused in the new route.
    // For example, for a route with dynamic params `/foo/:id`, when we
    // navigate between `/foo/1` and `/foo/2`, the same `Foo` component instance
    // will be reused, and this hook will be called when that happens.
    // has access to `this` component instance.
    //
    // The `if (this.book)` below is for tests, I didn't observe this.book
    // being empty here in a real app.
    // Looks like in tests, there is no await for beforeMount, so we can get
    // here before the `loadBook` call.
    if (to.params.url_slug === from.params.url_slug) {
      // In this case, we're navigating within the same book.
      // We use this hook to change the current active page in the book model.
      if (this) {
        const isNavigatingToHighlight = !!to.query?.highlight

        this.getPageFromRoute(to)

        // Skip scrolling to top when navigating to a highlight/bookmark.
        // The `highlight` query param indicates the user clicked a bookmark/highlight
        // link (e.g., from the TOC sidebar). In this case, we preserve
        // the current scroll position so that the highlight scroll logic
        // in Content.vue (handleHighlight) can smoothly scroll to the target element.
        // Without this check, it resets to the top first, then scroll to the highlight,
        // causing a visual jump.
        if (!isNavigatingToHighlight) {
          this.scrollToTop()
        }

        // Restart progress timer after moving to another book page.
        if (!this.timersDisabled) {
          if (isNavigatingToHighlight) {
            assertNotNull(this.readingProgress).restartTrackingWithoutRestore()
          } else {
            assertNotNull(this).restartTracking()
          }
        }

        if (to.meta.isDocContentPage) {
          this.startReadingTime()
        }
      }
    } else {
      // Destroy the current ReadingProgress instance so we don't have two instances
      // on the next book.
      if (this && this.readingProgress) {
        this.readingProgress.destroy()
      }

      // `to` is a new book's route, therefore this is the end of this book's
      // session, and we need to log the reading time.
      await this.updateReadTimeAndSavePosition()
      // Since beforeMount() isn't called, we call loadDocument() here to load
      // the new book's data.
      await loadDocument(to)
    }
  }

  async initDocument(restorePosition: boolean): Promise<void> {
    if (this.isContentPage) {
      await this.startReadingTime()
    }

    this.readingProgress = new ReadingProgress(
      assertNotNull(this),
      restorePosition,
    )

    // Set the current page from current route.
    meta.setAppTitle(assertNotNull(this).title)

    // Start the 20 sec timer to add the document to the user library
    // or update the access time.
    this.restartTracking()
  }

  /**
   * Return whether this book is free to this user, taking experiment
   * 18484 (Free Book of the Day) into account.
   */
  get isFree18484(): boolean {
    return isFree18484(this)
  }

  /**
   * Start tracking the time spent in the doc page.
   *
   * It doesn't do anything if the tracking has already started.
   */
  async startReadingTime(): Promise<void> {
    if (this.readingStartTime) {
      return
    }

    this.readingStartTime = Date.now()
    this.isReadingTimePaused = document.hidden
    emitter.on('appVisibilityChange', this.handleVisibilityChange)

    // Start an interval for tracking the user's first 5 minutes reading this doc
    // for the user checklist experiment (#19055). There are two steps in the checklist
    // about reading a doc for 5 minutes. If this doc has already been used for one of
    // them, we don't count it again.
    const firstDocRead = storage.getItem(StorageKeys.checklistFirstDoc19055)
    if (
      checklist() &&
      !checklist()?.finishedReadingSteps &&
      this.url_slug !== firstDocRead &&
      !this.checklistReadTimeStepInterval19055
    ) {
      this.checklistReadTimeStepInterval19055 = setInterval(
        this.updateChecklistReadDocStep.bind(this),
        // This is an interval, not a timeout: we call it
        // every 1 minute. `updateChecklistReadDocStep` clears the
        // interval when the step is completed (which should take 5
        // minutes at most).
        60 * 1000,
      )
    }

    // Update the reading streak every 1 minute.
    const updateFrequencyMs = 60 * 1000
    this.streakTimer = new Timer(updateFrequencyMs, () =>
      this.updateReadingStreak(updateFrequencyMs),
    )
    if (!this.isReadingTimePaused) {
      this.streakTimer.start()
    }
  }

  /**
   * Update the reading streak data in the backend, and restart the streak timer if the streak is not completed.
   *
   * This method is passed as the callback for `this.streakTimer`, and it keeps restarting the timer until
   * the backend responds that the today's reading streak is completed.
   */
  private async updateReadingStreak(timeToAddMs: number): Promise<void> {
    const updatedData = await backend.updateReadingStreak(timeToAddMs)

    if (!this.streakTimer) {
      // It's possible that while waiting for the backend response, the user
      // navigated away from the page, which clears and removes the timer.
      return
    }

    this.streakTimer?.clear()

    if (updatedData.remaining_seconds <= 0) {
      // If `remaining_time` is 0, today's streak is completed,
      // so we stop sending requests.
      this.streakTimer = null
      return
    }

    if (this.isReadingTimePaused) {
      // If the reading time tracking is paused by the time
      // we get a response from the backend, just reset the
      // delay in the timer, but don't start it yet (it'll
      // be resumed once the reading time tracking is resumed,
      // in `this.resumeReadingTime`).
      this.streakTimer?.resetDelay()
    } else {
      this.streakTimer?.start()
    }
  }

  /**
   * Clear the reading streak timer and update the streak data with the timer's elapsed time.
   *
   * The reading streak data is updated every 1 minute by the `this.streakTimer` timer. If
   * the user leaves the pages before the current timer iteration, we call this method to
   * remove that timer and update the reading streak data with its elapsed time.
   */
  async updateReadingStreakAndClear(): Promise<void> {
    if (!this.streakTimer) {
      return
    }

    const remainingTime = this.streakTimer.remainingTime
    const elapsedTime = this.streakTimer.elapsedTime

    // Clear the timer, so that it doesn't trigger in the future.
    this.streakTimer.clear()
    this.streakTimer = null

    if (remainingTime > 0) {
      // If there's remaining time in the timer, update the streak data
      // with the timer's elapsed time.
      await backend.updateReadingStreak(elapsedTime)
    }
  }

  /**
   * Update the read time on mixpanel and save the reading position.
   */
  async updateReadTimeAndSavePosition(): Promise<void> {
    await Promise.all([
      this.updateReadingStreakAndClear(),
      this.saveReadingPositionFromScrollPosition(),
      this.updateReadTime(),
    ])
  }

  /**
   * Add 1 minute towards the progress of the `read_first` or `read_second`
   * steps in the user checklist (#19055).
   */
  private updateChecklistReadDocStep(): void {
    const isFirstReadingStep =
      !storage.getItem(StorageKeys.checklistFirstDoc19055) &&
      !checklist()?.steps.read_first.isCompleted

    const stepID: ChecklistStepID = isFirstReadingStep
      ? 'read_first'
      : 'read_second'

    checklist()?.steps[stepID].addProgress(1)

    // If the step is completed, clear the interval, and store the doc URL slug
    // in local storage, to avoid marking the `read_second` set in this same doc.
    if (checklist()?.steps[stepID].isCompleted) {
      clearInterval(this.checklistReadTimeStepInterval19055)
      if (isFirstReadingStep) {
        storage.setItem(StorageKeys.checklistFirstDoc19055, this.url_slug)
      }
    }
  }

  createTutorial(): BookTutorial | null {
    return null
  }

  createToc(): DocTocView {
    return new DocTocView(this)
  }

  /**
   * Keep the sidebar and header open.
   *
   * Depending on the scrolling position, we want to show the header at the top
   * of the page and toolbar at the bottom of the page.
   *
   * This function disallows hiding the header and toolbar when scroll is at top
   * or bottom of the page.
   */
  keepSidebarAndHeaderOpen(): boolean {
    const distanceToBottom =
      this.appEl.scrollHeight - window.innerHeight - this.appEl.scrollTop
    if (
      this.appEl.scrollTop < SCROLL_SHOW_THRESHOLD_MOBILE ||
      distanceToBottom < SCROLL_SHOW_THRESHOLD_MOBILE
    ) {
      return true
    }
    return false
  }

  get pageNum(): number {
    return this._pageNum
  }
  /**
   * Returns any page link data.
   */
  pageLink(pageIdx: number): any {
    const exists = pageIdx < this._pages.length && pageIdx >= 0

    const res = {
      exists: exists,
      title: exists ? this._pages[pageIdx].title : '',
      params: {
        name: this.routeNames().page,
        params: {
          url_slug: this.url_slug,
          page_url_slug: exists ? this._pages[pageIdx].url_slug : '',
        },
      },
    }

    return res
  }

  /**
   * Factory function to create chapter object
   * based on content_type value.
   */
  protected createChapter(content: Content): ContentView {
    return new ContentView(this, content)
  }

  /**
   * Get a `DocListItem` version of this doc (ie. the base properties without `content`).
   */
  get asListItem(): DocListItem {
    const result = {} as DocListItem
    _copyDoc(this, result)
    return result
  }

  /**
   * Document pages.
   */
  get pages(): ContentView[] {
    return this._pages
  }

  /**
   * Returns all bookmarks from the doc.
   */
  get bookmarks(): Bookmark[] {
    return this._pages.flatMap((page) => {
      const highlights = page.highlights.filter(
        (highlight) => highlight.highlight_type === 'bookmark',
      )
      return highlights.map((highlight) => {
        return {
          ...highlight,
          page_url_slug: page.url_slug,
        }
      })
    })
  }

  /**
   * Document pages map.
   *
   * Should be overridden in subclasses.
   * For example, in the book we have a map like this:
   *
   * return {
   *   'community': 'book.community',
   *   'thread': 'book.thread',
   *   'edit_thread': 'book.edit_thread',
   *   'highlights': 'book.highlights',
   *   'export': 'book.export',
   *   'page': 'book.page',
   *   'preview': 'book.preview',
   * }
   */
  routeNames(): Record<string, string> {
    throw new Error('Not implemented')
  }

  /*
   * Return an object with the values for the reading button
   * Should be overridden in subclasses.
   */
  startReadingButton(): any {
    throw new Error('Not implemented')
  }

  /*
   * Returns the title for the DocPreview page 'About ${Book | Episode}
   * Should be overridden in subclasses.
   */
  get aboutTitle(): string {
    throw new Error('Not implemented')
  }

  /*
   * Return a boolean, that determines if we show the download button on the DocPreviewPage.
   *  Should be overridden in subclasses.
   */
  get showDownload(): boolean {
    throw new Error('Not implemented')
  }

  /*
   * Returns the title for the full summary item of the reading page TOC.
   * Should be overridden in subclasses.
   */
  public get fullSummaryItemText(): string {
    throw new Error('Not implemented')
  }

  /*
   * Current selected page.
   */
  get currentPage(): ContentView {
    return this._pages[this._pageNum]
  }

  /*
   * Current exercise for the selected page.
   */
  get currentExercise(): OpenQuestionsView {
    if (!this.isCurrentPageOpenQuestions) {
      throw new Error('Can not return current page as open questions')
    }
    return this._pages[this._pageNum] as OpenQuestionsView
  }

  /**
   * Returns true if specified `_pageNum` equals to the
   * current selected page number.
   */
  isCurrentPage(page: ContentView): boolean {
    const route = router().currentRoute.value
    if (route.name !== this.routeNames().page) {
      // This method is also called when we reach the doc community,
      // in that case there is no 'page_url_slug' in params and we
      // return "false" for doc pages.
      return false
    }
    return this._pageNum === page.order
  }

  isCurrentFirstPage(): boolean {
    return (
      this._pageNum === 0 &&
      router().currentRoute.value.name === this.routeNames().page
    )
  }

  isCurrentReadingPage(): boolean {
    return (
      this._pageNum > 0 &&
      router().currentRoute.value.name === this.routeNames().page
    )
  }

  /**
   * Returns true current page is 'community'.
   */
  isCurrentCommunity(): boolean {
    return router().currentRoute.value.name === this.routeNames().community
  }

  isCurrentHighlights(): boolean {
    return router().currentRoute.value.name === this.routeNames().highlights
  }

  /*
   * Returns true if this content has 'chapter' type.
   */
  get isCurrentPageChapter(): boolean {
    return this.currentPage.content_type === 'chapter'
  }

  /**
   * Returns true current page is 'doc_export'.
   */
  isCurrentPageExport(): boolean {
    return router().currentRoute.value.name === this.routeNames().export
  }

  /*
   * Returns true if this content has 'open_questions' type.
   */
  get isCurrentPageOpenQuestions(): boolean {
    return (
      this.currentPage.content_type === 'open_questions' ||
      this.currentPage.content_type === 'quiz'
    )
  }

  /*
   * Returns true if this content has 'open_questions' type.
   */
  isPageOpenQuestions(page: ContentView): boolean {
    return (
      page.content_type === 'open_questions' || page.content_type === 'quiz'
    )
  }

  /**
   * Updates current page number depending on the route.
   * So when we click the `/book/book-id/page/3` link,
   * for example, the `this._pageNum` is set to 3.
   *
   * Initially we set it to 0 (the first page).
   *
   * This is called from a root BookSummaryPage component
   * after loading the book and on route updates.
   */
  public getPageFromRoute(route: RouteLocationNormalized): void {
    if ('page_url_slug' in route.params && route.params.page_url_slug) {
      const page = this._pagesMap[route.params.page_url_slug as string]
      if (page) {
        this._pageNum = page.order
      } else {
        console.log('Wrong route, redirect to the first page.')
        // Wrong route, redirect to the first page.
        // We do the redirect here and not just set the this._pageNum
        // so we don't leave the non-existing URL in the browser
        // address field.
        router().push({ name: this.routeNames().page })
      }
    } else {
      this._pageNum = 0
    }
  }

  /**
   * Returns true if specified `_pageNum` is marked as completed.
   */
  isCompletedPage(page: ContentView): boolean {
    return page.completed !== null && page.completed !== undefined
  }

  /**
   * Returns true if all chapters under full summary are completed.
   */
  get isCompletedFullSummary(): boolean {
    for (let idx = 1; idx < this._pages.length; idx++) {
      const page = this._pages[idx]
      if (page.completed === null) {
        return false
      }
    }
    return true
  }

  /**
   * Returns true if end-of-content should be rendered on the chapter page.
   */
  public get isChapterEOC(): boolean {
    if (this.isFree18484) {
      // No end-of-content if the doc is free.
      return false
    }
    // If plan is free - render end-of-content, otherwise - not.
    return auth.isFreePlan()
  }

  /**
   * Returns true if the current page is the doc preview page.
   */
  public get isPreviewPage(): boolean {
    const currentPath = router().currentRoute.value.path

    // Check if the current path ends with `/preview`
    return Boolean(currentPath.match(/\/preview$/))
  }

  public get hasHighlights(): boolean {
    return this.highlights.length > 0
  }

  /**
   * Get annotations.
   */
  public get highlights(): AnnotationView[] {
    const result: AnnotationView[] = []
    for (let i = 0; i < this._pages.length; i++) {
      const item = this._pages[i]
      for (let j = 0; j < item.highlights.length; j++) {
        const h = item.highlights[j]
        result.push(new AnnotationView(item, h))
      }
    }
    return result
  }

  /**
   * Returns previous page link data.
   */
  get prevLink(): { title: string; route: RouteLocationRaw } | null {
    const exists = this._pageNum > 0
    if (!exists) {
      return null
    }

    return {
      title: this._pages[this._pageNum - 1].title,
      route: {
        name: this.routeNames().page,
        params: {
          url_slug: this.url_slug,
          page_url_slug: this._pages[this._pageNum - 1].url_slug,
        },
      },
    }
  }

  /**
   * Return the link for "preview" page.
   */
  get previewLink(): RouteLocationRaw {
    return {
      name: this.routeNames().preview,
      params: {
        url_slug: this.url_slug,
      },
    }
  }

  /**
   * Returns next page link data.
   */
  get nextLink(): { title: string; route: RouteLocationRaw } | null {
    const exists = this._pageNum < this._pages.length - 1
    if (!exists) {
      return null
    }

    return {
      title: this._pages[this._pageNum + 1].title,
      route: {
        name: this.routeNames().page,
        params: {
          url_slug: this.url_slug,
          page_url_slug: this._pages[this._pageNum + 1].url_slug,
        },
      },
    }
  }

  /**
   * Start link for the book, according to the tracked reading position.
   */
  startLinkParams(): Record<string, unknown> {
    return {
      name: this.routeNames().page,
      params: {
        url_slug: this.url_slug,
        page_url_slug: this.user.position_chapter,
      },
    }
  }

  /**
   * Style for the "Done" button.
   * (see PrevNext.vue)
   */
  get doneStyle(): 'link' | 'button' {
    return 'link'
  }

  /**
   * Text to display below the "Done" button.
   */
  get doneText(): string {
    throw new Error('Not implemented')
  }

  /**
   * Handle the "Done" button.
   */
  public async doneClick(): Promise<void> {
    throw new Error('Not implemented')
  }

  /* Delete a highlight.
   *
   * Used in the highlights list on the book highlights page.
   * Looks through the document pages to find the given `highlight`
   * and remove it.
   * Sends a backend request to delete the highlight and removes it
   * from the highlights list to update the UI.
   */
  public async deleteHighlight(highlight: AnnotationView): Promise<Annotation> {
    for (const page of this._pages) {
      for (let i = 0; i < page.highlights.length; i++) {
        if (page.highlights[i].id === highlight.id) {
          const annotation = await backend.deleteHighlight(
            highlight.content,
            highlight,
          )
          page.highlights.splice(i, 1)
          return annotation
        }
      }
    }
    throw new Error('Error during deleting highlight from highlight list')
  }

  /* Copy a highlight.
   *
   * Copies highlight content to the clipboard.
   */
  public async copyHighlight(highlight: AnnotationView): Promise<void> {
    try {
      await navigator.clipboard.writeText(highlight.quote)
    } catch (err) {
      throw new Error('Error during copying highlight to clipboard')
    }
  }

  /**
   * This method saves doc progress by marking chapters completed.
   *
   * We consider the text chapter as completed if we stay on the page
   * for 20 seconds. See the ReadingProgress class.
   */
  async saveProgress(chapter?: ContentView): Promise<void> {
    if (auth.isFreePlan() && !this.isFree18484) {
      // If the user is on free plan and the doc is not free,
      // don't track the reading progress (because content is
      // not fully avaiable, part is hidden behind the end-of-content block).
      return
    }

    if (!this.isContentPage) {
      // Don't save reading progress for non-doc pages: preview, PDF download, discussion, etc.
      return
    }

    // The chapter can change before the call so we capture the current
    // chapter to use it for: the "Next" button completes the chapter it is
    // leaving, and the audio player completes the chapter it has finished playing.
    const page = chapter || this.currentPage
    const originalProgress = this.user.progress

    const response = await backend.saveProgress(page)
    // Keep this model in sync with the back end.
    this.user.progress = response.progress

    Mixpanel.trackContentCompleted(this)

    if (this.user.progress === 100) {
      if (originalProgress < 100) {
        // Send the "Doc Completed" event if this is the first time we've
        // reached 100%. See https://github.com/shortformhq/main/issues/16955.
        Mixpanel.trackDocCompleted(this)
      }

      // The setFirstDoc routine only sets the property if it doesn't exist,
      // so we don't need to check originalProgress.
      Mixpanel.setFirstDoc('Completed', this.title, this.doc_type)
    }

    // Note: it is better to return content from backend save progress
    // API, so we could just reuse backend logic here and do something
    // like content.completed = response.data.completed
    if (page.content_type === 'chapter') {
      // Mark chapters as completed.
      const now = new Date()
      page.completed = now.toISOString()

      // Track "First <doc_type> Summary Completed" if this is the first page
      // (ie. the "1-Page Summary" page) and it's not an article.
      if (page.order === 0 && this.doc_type !== 'article') {
        Mixpanel.setFirstDoc('Summary Completed', this.title, this.doc_type)
      }
    }
  }

  async completeAndSaveProgress(): Promise<void> {
    if (auth.isFreePlan() && !this.isFree18484) {
      // If the user is on free plan and the doc is not free,
      // don't track the reading progress (because content is
      // not fully avaiable, part is hidden behind the end-of-content block).
      return
    }

    if (!this.isContentPage) {
      // Don't save reading progress for non-doc pages: preview, PDF download, discussion, etc.
      return
    }

    const originalProgress = this.user.progress

    const response = await backend.completeAndSaveProgress(this)
    // Keep this model in sync with the back end.
    this.user.progress = response.progress

    Mixpanel.trackContentCompleted(this)

    if (originalProgress < 100) {
      // Send the "Doc Completed" event if this is the first time we've
      // reached 100%. See https://github.com/shortformhq/main/issues/16955.
      Mixpanel.trackDocCompleted(this)
    }

    // The setFirstDoc routine only sets the property if it doesn't exist,
    // so we don't need to check originalProgress.
    Mixpanel.setFirstDoc('Completed', this.title, this.doc_type)
  }

  /*
   * The `#app` HTML element, scrollable view.
   */
  get appEl(): HTMLElement {
    return assertNotNull(document.getElementById('app'))
  }

  /*
   * Restores reading position within the page. Scrolls to the saved percent
   * value of the page height. When a fragment link is set, it scrolls to the
   * element with the same id instead.
   */
  restoreReadingPosition(): void {
    // Ignore position restore if the route contains a fragment link.
    const fragment = router().currentRoute.value.hash
    if (Boolean(fragment)) {
      const element = document.getElementById(fragment.slice(1))

      // It does not suffice to check for truthiness, because TS
      // does not think of `Boolean(null)` as false...
      if (element !== null && Boolean(element)) {
        element.scrollIntoView()
      }
      return
    }

    // Do not restore position for the preview page
    const currentRoutePath = router().currentRoute.value.path
    if (currentRoutePath && currentRoutePath.includes('preview')) {
      return
    }

    // Do not restore position if chapter don't match
    if (this.user.position_chapter !== this.currentPage.url_slug) {
      return
    }

    const y = (this.appEl.scrollHeight * this.user.position_percent) / 100
    this.appEl.scroll(0, y)
  }

  /*
   * Get current reading position within the page.
   */
  getPositionChapterPercent(): number {
    // For Safari-based browsers scrollTop can be negative (#2617)
    // Just return 0 in this case
    if (this.appEl.scrollTop < 0) {
      return 0
    }
    return Math.round((this.appEl.scrollTop / this.appEl.scrollHeight) * 100)
  }

  /**
   * Scroll to the top of the page.
   *
   * We use this when going from a book page to the next. The default
   * behavior is to just preserve the scroll position, so the user
   * would navigate to the next page but the scroll would stay at
   * the bottom.
   */
  scrollToTop(): void {
    this.appEl.scrollTop = 0
  }

  /**
   * Check if current route is a content page.
   *
   * We have some non-content pages in some docs, for example, PDF download page and
   * Discussions page. We don't need to save the progress on these pages.
   * @returns true: if the current route is a content page
   */
  get isContentPage(): boolean {
    const currentRoute = router().currentRoute.value
    return currentRoute.meta.isDocContentPage || false
  }

  /**
   * Returns 'true' if the DocGroup that this Doc is a part of
   * can be followed.
   */
  isDocGroupFollowable(): boolean {
    return false
  }

  /**
   * Save doc reading position using the page scroll amount.
   */
  async saveReadingPositionFromScrollPosition(): Promise<void> {
    const position_percent = this.getPositionChapterPercent()
    await this.saveReadingPosition(position_percent)
  }

  /**
   * Save doc reading position
   */
  async saveReadingPosition(position_percent: number): Promise<void> {
    // Don't do anything if nothing changed
    if (
      this.user.position_chapter === this.currentPage.url_slug &&
      Math.round(this.user.position_percent - position_percent) === 0
    ) {
      return
    }

    // Don't save the position on non-doc pages: download PDF, discussions etc
    if (!this.isContentPage) {
      return
    }

    this.user.position_chapter = this.currentPage.url_slug
    this.user.position_percent = position_percent

    // Update cached doc data
    // We update cache doc data before making a network call
    // so that the cache is always up-to-date regardless of
    // whether the network call succeeds or fails (user goes offline in the process)
    await updateDocsUserDataCache(this.id, this.user)
    await updateUserDocsCache(this.asListItem)

    const response = await backend.saveReadingPosition(
      this.currentPage,
      position_percent,
    )

    // Accessed has changed, and `list` might have changed too.
    this.user.accessed = response.accessed
    this.user.list = response.list
    await updateDocsUserDataCache(this.id, this.user)
  }

  async registerDocExport(): Promise<void> {
    if (this.doc_export_stats && !this.doc_export_stats.exported) {
      this.doc_export_stats.exported = true
      if (!this.is_free) {
        // Free books don't count into the PDF download limit
        // (But free-book-of-the-day-18484 does count)
        this.doc_export_stats.limit.exports += 1
      }
    }
  }

  /**
   * Update doc export stats.
   */
  async getDocExportStats(): Promise<DocExportStats> {
    if (!this.doc_export_stats) {
      const data = await backend.getDocExportStats(this.id)
      this.doc_export_stats = data
      return data
    }
    return this.doc_export_stats
  }

  /**
   * Prefix for PDF export suggested filenames (matches API Content-Disposition naming).
   */
  get exportPdfFileNamePrefix(): string {
    const kind = assertNotNull(docTypeLabel[this.doc_type].exportKind)
    return `Shortform ${capitalizeString(kind)}`
  }

  /**
   * Main heading on the doc export page when the default download state applies.
   */
  get exportDownloadPageTitle(): string {
    return `Download ${docTypeLabel[this.doc_type].exportKind}`
  }

  /**
   * Returns true if the end-of-content version of the community should be rendered.
   */
  public get isCommunityEOC(): boolean {
    if (this.is_free) {
      // No end-of-content if the book is free.
      return false
    }
    // If plan is free - render end-of-content, otherwise - not.
    return auth.isFreePlan()
  }

  /**
   * Move book to another list.
   */
  async moveTo(list: string): Promise<void> {
    const result = await backend.moveBookToList(this, list)
    this.user.list = result.list
    await updateDocsUserDataCache(this.id, result)
    const doc = {} as Doc
    _copyDoc(this, doc)
    await updateUserDocsCache(doc)
  }

  /**
   * Returns true current page is 'thread'.
   */
  isCurrentThread(): boolean {
    return router().currentRoute.value.name === this.routeNames().thread
  }

  /**
   * Returns a CSS class used to identify to which
   * doc type a book reading page belongs to.
   */
  public get readingCssClass(): string {
    return `book-reading--${this.doc_type}`
  }

  /**
   * Returns true if the early access banner should be rendered
   */
  get showEarlyAccessBanner(): boolean {
    return this.is_ai
  }

  /**
   * Scroll to the given selector but keep an offset at the top so that the header
   * doesn't cover the element.
   */
  scrollToTargetAdjusted(selector: string): void {
    // The element we want to scroll to.
    const scrollToTarget = document.querySelector(selector)
    // The element that gets scrolled.
    const scrollElement = document.getElementById('app')
    if (scrollToTarget && scrollElement) {
      const headerOffset = 120
      const scrollToTargetPosition = scrollToTarget.getBoundingClientRect().top
      const offsetPosition =
        scrollToTargetPosition + scrollElement.scrollTop - headerOffset

      scrollElement?.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }
  }

  /**
   * Scroll to highlight .
   */
  scrollToHighlight(highlight: Annotation): void {
    const selector = `[data-annotation-id="${highlight.id}"]`

    const highlightElement = document.querySelector(selector)
    if (highlightElement) {
      // We add a short timeout as the scroll / element position reported by
      // Safari is wrong initially, and such we'd scroll to a wrong position
      // without this delay.
      setTimeout(() => this.scrollToTargetAdjusted(selector), 1)
    } else {
      if (auth.isPaidPlan()) {
        // We only send this message if the user is on a paid plan, as this
        // is a rare case and we don't want to clutter the Sentry dashboard with the following flow:
        // 1. Started trial, made some highlights
        // 2. Cancelled trial
        // 3. Tries to go to the highlight, but it's behind the paywall
        Sentry.withScope((scope) => {
          scope.setExtra('excerpt_text', highlight.quote)
          scope.setExtra('original_text', highlight.text)
          Sentry.captureMessage(
            `Could not find highlight. Text not found in summary. Highlight ID: ${highlight.id}`,
          )
        })
      }
    }
  }

  /**
   * Returns true if the AI content label should be rendered.
   */
  get showAiLabel(): boolean {
    return this.is_ai
  }
}

/**
 * Copy source `doc` properties into `target`.
 *
 * We use this method when constructing the `DocView` object based
 * on the backend API response, to make sure that all expected fields
 * are present.
 *
 * Note: the need in copying could be removed by using some validation
 * library (https://github.com/sideway/joi ?).
 * In that case we could just validate and store the incoming `doc`
 * object in the `DocView` constructor and use it directly.
 *
 * Related discussion: https://github.com/shortformhq/main/pull/2966#discussion_r676190521.
 */
function _copyDoc(doc: DocListItem, target: DocListItem): void {
  target.id = doc.id
  target.title = doc.title
  target.author = doc.author
  target.blurb = doc.blurb
  target.cover_image = doc.cover_image
  target.url_slug = doc.url_slug
  target.public_url_slug = doc.public_url_slug
  target.is_free = doc.is_free
  target.is_free_botd_18484 = doc.is_free_botd_18484
  target.amazon_link = doc.amazon_link
  target.amazon_image = doc.amazon_image
  target.tags = doc.tags
  target.has_audio = doc.has_audio
  target.doc_type = doc.doc_type
  target.popularity_read_count = doc.popularity_read_count
  target.popularity_user_count = doc.popularity_user_count
  target.created = doc.created
  target.first_published_at = doc.first_published_at
  target.episode_number = doc.episode_number

  target.forum_id = doc.forum_id

  target.original_publish_date = doc.original_publish_date
  target.original_url = doc.original_url
  target.original_words = doc.original_words
  target.summary_words = doc.summary_words

  target.audio = doc.audio
  target.publisher = doc.publisher
  target.doc_group = doc.doc_group
  target.is_ai = doc.is_ai
}

/**
 * Return whether this book is free to this user, taking experiment
 * 18484 (Free Book of the Day) into account.
 */
export function isFree18484(doc: DocListItem): boolean {
  if (doc.is_free) {
    // Totally free book.
    return true
  }
  if (doc.is_free_botd_18484 && showFreeBookOfTheDay18484()) {
    // Today's free book of the day, and the user is in variant B of the
    // experiment.
    return true
  }
  return false
}

export enum DocSortOption {
  title = 'title',
  first_published_at = 'first_published_at',
  accessed = 'accessed',
  viewed = 'viewed',
  popular = 'popular',
  recommended = 'recommended',
  none = 'none',
}

/**
 * Book data and logic to display book in components.
 */
export class DocsView implements DocsListView {
  private _docs: DocListItem[]
  private _filteredDocs: DocListItem[]

  // Sort option, 'title' is server-side default
  protected _sort: DocSortOption = DocSortOption.title

  // List to filter books by ('reading', 'complete', 'removed')
  private _list: string
  // Filter books by custom_list.
  private _custom_list: string
  // Selected tag to filter books by.
  private _tag: string
  // Filter books by is_favorite flag.
  private _is_favorite: boolean | null
  // Filter books by is_exported flag.
  private _is_exported: boolean | null
  // Show only books the user has not finished yet.
  private _only_in_progress: boolean
  // Book IDs to display first. This maps IDs to sort order.
  private _sort_at_top: Map<string, number>
  // Show CTA blocks for free users.
  protected _showCtaBlock: boolean | null

  // All tags.
  private _tags: string[]

  // Reference to views on the same page which share the same docs list.
  private _view_mediator: DocsViewMediator | null

  // Show items limit, managed by DocsScroll class.
  // This is a performance optimization, so we do not have to
  // render all documents at once.
  // Instead we show first few documents and then render more
  // on scroll.
  private _showLimit: number
  // Hard limit, show only up to `_limit` docs.
  private _limit?: number = undefined

  // Handles the doc search logic
  private _searcher: Search

  private _continueReading: boolean = false
  private _showActionsButton: boolean = false

  private _hasSetUserData: boolean = false

  constructor(
    docs: DocListItem[],
    tags: string[],
    defaultSort: DocSortOption = DocSortOption.title,
  ) {
    assertNotNull(docs)

    this._docs = docs as DocListItem[]
    this._filteredDocs = []
    this._view_mediator = null
    this._showLimit = SCROLL_INITIAL_SHOW_LIMIT
    this._limit = undefined

    // Create the searcher with the docs.
    this._searcher = new Search(this._docs, this.docTypeName())

    this._sort = defaultSort
    // List to filter the books by.
    this._list = ''
    this._custom_list = ''
    this._tags = tags
    this._tag = ''
    this._is_favorite = null
    this._is_exported = null
    this._only_in_progress = false
    this._sort_at_top = new Map()
    this._showCtaBlock = true
  }

  public docTypeName(): DocType {
    throw new Error('Not implemented')
  }

  /**
   * Whether to show free items at the top.
   * Customized in subclasses.
   */
  protected showFreeAtTheTop(): boolean {
    return false
  }

  async init(): Promise<void> {
    await this._updateFilteredDocs()
  }

  setUserData(docsUserData?: DocsUserDataResponse): void {
    const docsLength = this._docs.length
    for (let i = 0; i < docsLength; i++) {
      const doc = this._docs[i]
      const userData = docsUserData?.[doc.id]

      doc.user = userData || getEmptyUserData()
    }

    this._hasSetUserData = true
  }

  static async searchDocsLimited(
    docType: DocType,
    searchTerm: string,
    limit: number = 100,
  ): Promise<ApiResultDocs> {
    const response = await backend.getDocSearch(docType, searchTerm, limit)
    return response
  }

  /**
   * All documents.
   */
  get docs(): DocListItem[] {
    return this._docs
  }

  /**
   * Returns `true` after `this.setUserData` has been called.
   */
  get hasSetUserData(): boolean {
    return this._hasSetUserData
  }

  enableContinueReading(): void {
    this._continueReading = true
  }

  get continueReading(): boolean {
    return this._continueReading
  }

  enableActionsButton(): void {
    this._showActionsButton = true
  }

  showActionsButton(doc: DocListItem): doc is UserDocListItem {
    return this._showActionsButton && this.hasUserData(doc)
  }

  hasUserData(doc: DocListItem): doc is UserDocListItem {
    return doc.user !== undefined
  }

  /**
   * Filtered by text / tag / list books.
   */
  get filteredDocs(): DocListItem[] {
    if (this._showLimit) {
      return this._filteredDocs.slice(0, this._showLimit)
    }
    return this._filteredDocs
  }

  get dataLength(): number {
    return this._filteredDocs.length
  }

  // Show free books at the top.
  get hasRecommendations(): boolean {
    return this._docs.some((doc) => doc.rank !== undefined)
  }

  /**
   * Set the DocsViewMediator for this view.
   * (There should only be one, because all views on the page that share
   * a data source should share a DocsViewMediator.)
   * Called by the DocsViewMediator constructor.
   */
  setViewMediator(mediator: DocsViewMediator): void {
    this._view_mediator = mediator
  }

  get viewMediator(): DocsViewMediator | null {
    return this._view_mediator
  }

  /**
   * Set the limit of number of documents to show on the page.
   *
   * This also pre-loads the cover images for the next batch of documents, so
   * that they will be in the cache when the user scrolls down to actually load
   * them. (See #7442.)
   */
  setShowLimit(limit: number): void {
    // When we will show more docs than are currently on the page:
    if (this._showLimit < limit) {
      // Load images for the next docs so that they're in the cache when
      // the user scrolls down.
      if (limit === SCROLL_INITIAL_SHOW_LIMIT) {
        // This implies we are initializing the page. We should load the images
        // for the first `limit` docs.
        // (Alternative: we could only load the ones that aren't in the
        // viewport yet. We would need to do some calculation to determine
        // what's in the viewport and what's not.)
        this._loadImagesForDocs(this._filteredDocs.slice(0, limit))
      } else {
        // Otherwise, load the images for the docs we will be newly adding to
        // the page, plus the batch afterwards.
        this._loadImagesForDocs(
          this._filteredDocs.slice(
            limit - SCROLL_BATCH_SIZE,
            limit + SCROLL_BATCH_SIZE,
          ),
        )
      }
    }
    this._showLimit = limit
  }

  /**
   * Is the current view limited.
   *
   * Returns true if the ``_limit`` property was set.
   * This method is used to remove the Sort component from limited pages.
   */
  get isLimited(): boolean {
    return this._limit !== undefined
  }

  /**
   * Set documents limit.
   *
   * This method is used by special listing pages: new and popular books.
   * In order for the limit to work for, e.g. new books page with limit 20:
   * - sort docs by "Latest" (done from books view)
   * - get the first 20 documents from the sorted list
   * - invoke ``_updateFilteredDocs`` to propagate the limit to ``_filteredDocs`` field
   *
   */
  async setDocsLimit(limit: number): Promise<void> {
    this._limit = limit
    await this._updateFilteredDocs()
  }

  get sortOption(): DocSortOption {
    return this._sort
  }

  /**
   * Update filtered books list.
   */
  protected async _updateFilteredDocs(): Promise<void> {
    this._filteredDocs = this._sortDocs(this._filterDocs(this.docs))
    if (this._limit) {
      this._filteredDocs = this._filteredDocs.slice(0, this._limit)
    }
  }

  _filterDocs(docs: DocListItem[]): DocListItem[] {
    assertNotNull(docs)

    const searchTag = this._tag && this._tag.toLowerCase()
    const hasTag = (set: string[]): boolean =>
      set.some((tag) => tag.toLowerCase() === searchTag)

    const filtered = docs
      .filter(
        (doc) =>
          !this._list ||
          doc.user?.list === this._list ||
          (this._list === 'viewed' && doc.user?.position_percent) ||
          (this._list === 'history' &&
            (doc.user?.list === 'reading' || doc.user?.list === 'finished')),
      )
      .filter(
        (doc) =>
          !this._custom_list ||
          doc.user?.custom_lists.some(
            (list) => list.url_slug === this._custom_list,
          ),
      )
      .filter((doc) => !this._tag || hasTag(doc.tags))
      .filter(
        (doc) =>
          this._is_favorite === null ||
          doc.user?.is_favorite === this._is_favorite,
      )
      .filter(
        (doc) =>
          this._is_exported === null ||
          doc.user?.is_exported === this._is_exported,
      )
      .filter(
        (doc) =>
          !this._only_in_progress ||
          (doc.user?.list === 'reading' && doc.user?.progress !== 100),
      )

    return filtered
  }

  _compareTwoDocs(a: DocListItem, b: DocListItem): number {
    let field: keyof DocListItem | keyof DocUserData = 'first_published_at'
    let isAscending = false

    // Don't sort docs - We keep the order from the backend result
    if (this._sort === DocSortOption.none) {
      return 0
    }

    if (this._sort === DocSortOption.title) {
      field = 'title'
      isAscending = true
    } else if (this._sort === DocSortOption.popular) {
      // Number of distinct users who have read at least one book page.
      field = 'popularity_user_count'
      if (a[field] === b[field]) {
        // If number of users is equal, use the total number of pages read.
        field = 'popularity_read_count'
      }
    } else if (this._sort === DocSortOption.accessed) {
      field = 'accessed'
    } else if (this._sort === DocSortOption.viewed) {
      field = 'viewed'
    } else if (
      this._sort === DocSortOption.recommended &&
      this.hasRecommendations
    ) {
      field = 'rank'
      isAscending = true
    }

    let valA: string | number | Date
    let valB: string | number | Date

    if (field === 'viewed') {
      valA = a.user?.viewed ? Date.parse(a.user.viewed) : 0
      valB = b.user?.viewed ? Date.parse(b.user.viewed) : 0
    } else if (field === 'accessed') {
      valA = assertNotNull(a.user?.accessed)
      valB = assertNotNull(b.user?.accessed)
    } else if (field === 'first_published_at') {
      // Can be null for @shortform.com accounts that can see unpublished
      // guides using the direct link (or as preview in CMS).
      valA = a.first_published_at ?? 0
      valB = b.first_published_at ?? 0
    } else {
      valA = assertNotNull(a[field])
      valB = assertNotNull(b[field])
    }

    if (field === 'first_published_at' || field === 'accessed') {
      valA = new Date(valA)
      valB = new Date(valB)
    }

    if (valA === valB) {
      return 0
    }
    if (isAscending) {
      return valA > valB ? 1 : -1
    } else {
      return valB > valA ? 1 : -1
    }
  }

  /**
   * Sort the docs for display.
   * This has to deal with a few complications.
   * - The _sort_at_top map contains docs which should be first (if they
   *   exist), because the user just added them. Show in _sort_at_top order.
   * - Otherwise, if showFreeAtTheTop() is true, free items are before
   *   non-free items. Show in original order.
   * - All other items are sorted by _compareTwoDocs().
   */
  _sortDocs(docs: DocListItem[]): DocListItem[] {
    const isFreeFirst = this.showFreeAtTheTop()

    const topList = []
    const freeList = []
    const sortList = []

    for (const doc of docs) {
      if (this._sort_at_top.has(doc.id)) {
        topList.push(doc)
      } else if (isFreeFirst && isFree18484(doc)) {
        freeList.push(doc)
      } else {
        sortList.push(doc)
      }
    }

    topList.sort(
      (doc1, doc2) =>
        (this._sort_at_top.get(doc1.id) ?? 0) -
        (this._sort_at_top.get(doc2.id) ?? 0),
    )
    sortList.sort(this._compareTwoDocs.bind(this))

    return [...topList, ...freeList, ...sortList]
  }

  /**
   *  Url for cover image to show on list page.
   */
  getItemCoverUrl(doc: DocListItem): string | undefined {
    if (doc.cover_image) {
      return cacheBust(doc.cover_image)
    }
  }

  /**
   * Load the cover image for each doc in `docs`.
   * This is to pre-load the book covers to make it look smoother when scrolling down.
   */
  _loadImagesForDocs(docs: DocListItem[]): void {
    docs.forEach((doc) => {
      /* The attributes (such as crossorigin) used here should be kept in
       *  sync with the attributes loaded by the vue templates. See the img
       *  tag in files:
       *
       *  app/components/article/Card.vue
       *  app/components/book/card/BookCard.vue
       */
      if (this.getItemCoverUrl(doc)) {
        const imageElement = document.createElement('img')
        imageElement.src = this.getItemCoverUrl(doc) as string
        imageElement.crossOrigin = 'anonymous'
      }
    })
  }

  /**
   * Injects CTA blocks into docs list.
   *
   * We need to display the CTAs more regularly than just once, so it has
   * more of an impact.
   *
   * For books we display it:
   * - on the second row
   * - after each 4 rows of books (18 books between CTA blocks)
   *
   * For articles we display it:
   * - on the third row
   * - after each 3 rows of articles (10 articles between CTA blocks)
   *
   * Artuments:
   *   position - start CTA block position, different for books and articles.
   *   gap - how many docs are between two CTA blocks.
   */
  injectCtaBlocks(position: number, gap: number): void {
    // Inject at least one CTA block, to the first position.
    // If position is less than number of Docs, the loop below will add the
    // first CTA block too.
    if (this._filteredDocs.length < position) {
      const ctaBlock = createCtaBlockItem()
      this._filteredDocs.splice(position, 0, ctaBlock)
      return
    }

    let idx = position
    let totalDocs = this._filteredDocs.length

    while (idx <= totalDocs) {
      this._filteredDocs.splice(idx, 0, createCtaBlockItem(idx))

      // Current position + gap + 1 (to compensate counting from 0)
      idx = idx + gap + 1

      // Each injection makes this._filteredDocs array longer, to have correct
      // logic after a lot of injections, we need to take it into account.
      totalDocs += 1

      // Debugging.
      // console.log(`next: ${idx}; total: ${totalDocs}`)
    }
  }

  needInjectCtaBlocks(): boolean {
    if (!this._showCtaBlock) {
      return false
    }
    if (auth.isFreePlan() && this.filteredDocs.length > 0) {
      return true
    }
    return false
  }

  /*
   * Filter books by `search` string.
   */
  async filter(search: string): Promise<void> {
    this._searcher.searchTerm = search
    this._docs = (await this._searcher.search()) as DocListItem[]
    assertNotNull(this._docs)
    await this._updateFilteredDocs()
  }

  /**
   * Get the current search query.
   */
  get searchTerm(): string | null {
    return this._searcher.searchTerm
  }

  /*
   * Sort books by `sort` field.
   */
  async sort(sort: DocSortOption): Promise<void> {
    if (this._sort !== sort) {
      this._sort = sort
      await this._updateFilteredDocs()
    }
  }

  /**
   * Set the `_sort` property without updating the order of the docs.
   */
  setSort(sort: DocSortOption): void {
    this._sort = sort
  }

  /**
   * Set tag to filter by.
   */
  async setTag(tag: string): Promise<void> {
    if (this._tag !== tag) {
      this._tag = tag
      await this._updateFilteredDocs()
    }
  }

  /**
   * Get current tag filter.
   */
  get tag(): string {
    return this._tag
  }

  /**
   * Set list to filter by.
   */
  async setList(list: string): Promise<void> {
    if (this._list !== list) {
      this._list = list
      await this._updateFilteredDocs()
    }
  }

  /**
   * Set a Custom List to filter by.
   */
  async setCustomList(url_slug: string): Promise<void> {
    if (this._custom_list !== url_slug) {
      this._custom_list = url_slug
      await this._updateFilteredDocs()
    }
  }

  /**
   * Filter by is favorite.
   */
  async setIsFavorite(is_favorite: boolean | null): Promise<void> {
    if (this._is_favorite !== is_favorite) {
      this._is_favorite = is_favorite
      await this._updateFilteredDocs()
    }
  }

  /**
   * Filter books to only show the ones the user has not finished yet.
   *
   * Filter for "Reading Now" block on Discovery page(Continue Reading Foundations #19038).
   * A book counts as finished when it is in the Finished list or when its
   * progress reached 100%.
   */
  async setOnlyInProgress(only_in_progress: boolean): Promise<void> {
    if (this._only_in_progress !== only_in_progress) {
      this._only_in_progress = only_in_progress
      await this._updateFilteredDocs()
    }
  }

  /**
   * Filter books to only show books downloaded to persistent storage.
   *
   * Downloads to persistent storage are implemented in native apps only.
   * On web, this filter results in an empty book list.
   */
  async filterByOffline(): Promise<void> {
    this._filteredDocs = await getOfflineBooks(this.docs)
  }

  /**
   * Filter books to show only books available for free.
   */
  filterByFree(): void {
    this._filteredDocs = this.docs.filter((doc) => isFree18484(doc))
  }

  /**
   * Remove documents with IDs in the excluded set from the source data.
   * Used to hide owned books from discovery pages, as if they were never loaded.
   */
  excludeByIds(excludedIds: Set<string>): void {
    this._docs = this._docs.filter((doc) => !excludedIds.has(doc.id))
  }

  /**
   * Filter books to only show books that have been exported
   * (PDF/Kindle downloads).
   */
  async filterByExports(): Promise<void> {
    this._is_exported = true
    await this._updateFilteredDocs()
  }

  /**
   * Add a document to the "sort these first" map.
   * Newest entries will be sorted earliest.
   * We don't make any attempt to remove old docs from this map.
   */
  private async addSortAtTopDoc(doc: DocListItem): Promise<void> {
    // Increase the index number of each entry by 1.
    this._sort_at_top.forEach((val, key, map) => map.set(key, val + 1))
    // Now insert the new doc at position zero. If it was already in
    // the map, this will update it to zero, leaving a gap. Gaps are ok.
    this._sort_at_top.set(doc.id, 0)
    await this._updateFilteredDocs()
  }

  /**
   * Get current list filter.
   */
  get list(): string {
    return this._list
  }

  /**
   * Get current Custom List filter.
   */
  get customList(): string {
    return this._custom_list
  }

  /**
   * Get is-favorite flag.
   */
  get isFavorite(): boolean | null {
    return this._is_favorite
  }

  /**
   * All tags to display in the categories filter.
   */
  get tags(): string[] {
    return this._tags
  }

  /**
   * A list of tags for the specified `book`.
   */
  docTags(doc: DocListItem): string[] {
    return doc.tags.sort()
  }

  /**
   * First tag for the doc.
   */
  firstTag(doc: DocListItem): string {
    return doc.tags.length ? doc.tags[0] : ''
  }

  /**
   * First tag for the doc, short version for mobile.
   *
   * We only keep the first part of the tag before the slash.
   */
  firstTagShort(doc: DocListItem): string {
    const firstTag = this.firstTag(doc)
    const shortTag = firstTag.split('/')[0]
    return shortTag ? shortTag : firstTag
  }

  /**
   * Returns 'true' if the docSort component should be visible.
   */
  isDocSortVisible(): boolean {
    return !this.isLimited && this._sort !== DocSortOption.none
  }

  /**
   * Move book to another list.
   */
  async moveTo(doc: UserDocListItem, list: string): Promise<void> {
    const origlist = doc.user.list
    const result = await backend.moveBookToList(doc, list)
    doc.user.list = result.list
    await updateDocsUserDataCache(doc.id, result)
    await updateUserDocsCache(doc)
    // Re-sort this list.
    await this._updateFilteredDocs()
    if (this._view_mediator) {
      // Re-sort the source list if it's not this one.
      if (origlist && this.list !== origlist) {
        this._view_mediator.callForNamedList(origlist, this, async (view) => {
          await view._updateFilteredDocs()
        })
      }
      // Re-sort the destination list(s), making sure the doc goes to the top.
      // (This calls _updateFilteredDocs().)
      if (doc.user.list) {
        this._view_mediator.callForNamedList(
          doc.user.list,
          this,
          async (view) => {
            await view.addSortAtTopDoc(doc)
          },
        )
      }
    }
  }

  /**
   * If this list has a sibling which is a "Favorites" list, update that.
   *
   * We do *not* update this list itself, even if it is a "Favorites" list.
   * If the user un-favorites a card on the "Favorites" list, we want to
   * leave the card in place, so they have a chance to change their mind.
   */
  async updateRelatedFavorites(): Promise<void> {
    if (this._view_mediator && !this.isFavorite) {
      this._view_mediator.callForFavorites(
        this,
        async (view) => await view._updateFilteredDocs(),
      )
    }
  }

  /**
   * Update the Custom Lists of the given `doc`.
   */
  async updateDocCustomLists(
    doc: UserDocListItem,
    newLists: CustomList[],
  ): Promise<void> {
    doc.user.custom_lists = newLists
    await this._updateFilteredDocs()
  }

  listingClass(doc: DocListItem): string {
    if (auth.isPaidPlan()) {
      return ''
    }
    if (!isFree18484(doc)) {
      return 'eoc'
    }
    return ''
  }
}

/**
 * A group of DocsViews on the same page which share a common data source.
 *
 * This permits lists to update each other. For example, when a Doc is
 * moved between lists, we need to refresh both of them.
 *
 * Pass in a function (receiver: DocsView, sender: DocsView) => void.
 */
export class DocsViewMediator {
  private _views: DocsView[]

  constructor(views: DocsView[]) {
    this._views = [...views]

    for (const view of this._views) {
      view.setViewMediator(this)
    }
  }

  /** Invoke function for all related lists. */
  async callForAll(
    sender: DocsView,
    func: (receiver: DocsView, sender: DocsView) => Promise<void>,
  ): Promise<void> {
    for (const view of this._views) {
      await func(view, sender)
    }
  }

  /** Invoke function for all related list which are named lists. */
  async callForLists(
    sender: DocsView,
    func: (receiver: DocsView, sender: DocsView) => Promise<void>,
  ): Promise<void> {
    for (const view of this._views) {
      if (view.list) {
        await func(view, sender)
      }
    }
  }

  /** Invoke function for the related list with a given name. */
  async callForNamedList(
    list: string,
    sender: DocsView,
    func: (receiver: DocsView, sender: DocsView) => Promise<void>,
  ): Promise<void> {
    for (const view of this._views) {
      if (view.list === list) {
        await func(view, sender)
      }
    }
  }

  /** Invoke function for the related "favorites" list. */
  async callForFavorites(
    sender: DocsView,
    func: (receiver: DocsView, sender: DocsView) => Promise<void>,
  ): Promise<void> {
    for (const view of this._views) {
      if (view.isFavorite) {
        await func(view, sender)
      }
    }
  }
}

/**
 * Return a new `DocUserData` object with default values.
 */
export function getEmptyUserData(): DocUserData {
  return {
    accessed: null,
    viewed: null,
    audio_position: 0,
    custom_lists: [],
    is_favorite: false,
    is_exported: false,
    is_read: false,
    list: null,
    position_chapter: null,
    position_percent: 0,
    progress: 0,
  }
}

export function getHighlightPublicLink(
  annotation: Annotation,
  content: AnnotationContent | ContentView,
): string {
  const location = router().resolve({
    name: 'public_highlight',
    params: {
      book_url_slug: content.doc.url_slug,
      highlight_id: annotation.id,
    },
  })
  let public_link = location.href
  const origin = window.location.origin

  // It doesn't work with localhost URLs, so we only handle dev and prod.
  if (origin.includes('dev.shortform.com')) {
    public_link = 'https://dev.shortform.com' + public_link
  } else {
    public_link = 'https://www.shortform.com' + public_link
  }
  return public_link
}

/* Returns the given url with an extra '?x' at the end.
 *
 * This is being used to make browsers not use cached versions of
 * images that don't have proper CORS headers in their responses.
 *
 * `cacheBust`, and all calls to it, can be removed after enough
 * time that most users no longer have such responses cached.
 * See #8374 for details.
 */
export function cacheBust(url: string): string {
  return url + '?x'
}

export function pngBigCoverUrl(coverImage: string): string | undefined {
  if (coverImage.endsWith('png')) {
    return coverImage.replace(new RegExp('.png$'), '@8x.png')
  }

  return coverImage
    .replace('/covers/', '/covers/png/')
    .replace(new RegExp('.jpg$'), '@8x.png')
}

export function filteredDocsByDocType(
  response: Doc[],
  doc_type: DocType,
): ApiResultDocs {
  const docs = response.filter((doc) => doc.doc_type === doc_type)

  return {
    data: docs,
  }
}

type DocTypeLabelType = {
  [key in ListItemType]: {
    singular: string
    plural: string
    exportKind?: 'article' | 'summary'
  }
}

export const docTypeLabel: DocTypeLabelType = {
  article: {
    singular: 'article',
    plural: 'articles',
    exportKind: 'article',
  },
  book: {
    singular: 'book',
    plural: 'books',
    exportKind: 'summary',
  },
  podcast_episode: {
    singular: 'podcast episode',
    plural: 'podcast episodes',
  },
  podcast: {
    singular: 'podcast',
    plural: 'podcasts',
  },
  collection: {
    singular: 'collection',
    plural: 'collections',
  },
  custom_list: {
    singular: 'my list',
    plural: 'my lists',
  },
}
